import axios from 'axios';

class GlitchRecorderService {
  constructor() {
    this.isRecording = false;
    this.logs = [];
    this.networkErrors = [];

    this.nativeConsoleError = null;
    this.nativeConsoleWarn = null;
    this.axiosInterceptorId = null;
    this.onerrorHandler = null;
    this.unhandledRejectionHandler = null;
  }

  start() {
    if (this.isRecording) {
      console.log('GlitchRecorder is already recording.');
      return;
    }

    this.isRecording = true;
    this.logs = [];
    this.networkErrors = [];

    // 1. Intercept console.error
    this.nativeConsoleError = console.error;
    console.error = (...args) => {
      if (this.isRecording) {
        this.logs.push({
          time: new Date().toLocaleTimeString(),
          type: 'error',
          msg: args.map(a => (typeof a === 'object' ? this.safeStringify(a) : String(a))).join(' ')
        });
      }
      if (this.nativeConsoleError) {
        this.nativeConsoleError.apply(console, args);
      }
    };

    // 2. Intercept console.warn
    this.nativeConsoleWarn = console.warn;
    console.warn = (...args) => {
      if (this.isRecording) {
        this.logs.push({
          time: new Date().toLocaleTimeString(),
          type: 'warn',
          msg: args.map(a => (typeof a === 'object' ? this.safeStringify(a) : String(a))).join(' ')
        });
      }
      if (this.nativeConsoleWarn) {
        this.nativeConsoleWarn.apply(console, args);
      }
    };

    // 3. Intercept window uncaught errors
    this.onerrorHandler = (message, source, lineno, colno, error) => {
      if (this.isRecording) {
        this.logs.push({
          time: new Date().toLocaleTimeString(),
          type: 'uncaught_error',
          msg: `${message} at ${source}:${lineno}:${colno}`
        });
      }
    };
    window.addEventListener('error', this.onerrorHandler);

    // 4. Intercept unhandled promise rejections
    this.unhandledRejectionHandler = (event) => {
      if (this.isRecording) {
        this.logs.push({
          time: new Date().toLocaleTimeString(),
          type: 'unhandled_rejection',
          msg: event.reason ? (typeof event.reason === 'object' ? this.safeStringify(event.reason) : String(event.reason)) : 'Unhandled Rejection'
        });
      }
    };
    window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);

    // 5. Intercept Axios response errors
    this.axiosInterceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        if (this.isRecording) {
          const errorInfo = {
            time: new Date().toLocaleTimeString(),
            url: error.config ? error.config.url : 'unknown',
            method: error.config ? error.config.method : 'unknown',
            status: error.response ? error.response.status : (error.message || 'NETWORK_ERROR'),
            statusText: error.response ? error.response.statusText : '',
            responseData: error.response && error.response.data ? this.safeStringify(error.response.data) : null
          };
          this.networkErrors.push(errorInfo);
        }
        return Promise.reject(error);
      }
    );

    console.log('🔴 GlitchRecorder: Recording started...');
  }

  stopAndSend() {
    if (!this.isRecording) {
      console.log('GlitchRecorder is not recording.');
      return { logs: this.logs, networkErrors: this.networkErrors };
    }

    this.isRecording = false;

    // Restore console methods
    if (this.nativeConsoleError) {
      console.error = this.nativeConsoleError;
      this.nativeConsoleError = null;
    }
    if (this.nativeConsoleWarn) {
      console.warn = this.nativeConsoleWarn;
      this.nativeConsoleWarn = null;
    }

    // Remove window event listeners
    if (this.onerrorHandler) {
      window.removeEventListener('error', this.onerrorHandler);
      this.onerrorHandler = null;
    }
    if (this.unhandledRejectionHandler) {
      window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
      this.unhandledRejectionHandler = null;
    }

    // Eject Axios interceptor
    if (this.axiosInterceptorId !== null) {
      axios.interceptors.response.eject(this.axiosInterceptorId);
      this.axiosInterceptorId = null;
    }

    const capturedData = {
      logs: this.logs,
      networkErrors: this.networkErrors,
      timestamp: new Date().toISOString()
    };

    // Send payload to SalesIQ context
    if (window.$zoho && window.$zoho.salesiq) {
      try {
        const consoleLogPayload = this.logs.length > 0 
          ? JSON.stringify(this.logs) 
          : 'No console errors/warnings captured during recording';
        const networkErrorPayload = this.networkErrors.length > 0 
          ? JSON.stringify(this.networkErrors) 
          : 'No failed network calls captured during recording';

        if (window.$zoho.salesiq.visitor && typeof window.$zoho.salesiq.visitor.info === 'function') {
          window.$zoho.salesiq.visitor.info({
            'Recorded_Console_Logs': consoleLogPayload,
            'Recorded_Network_Errors': networkErrorPayload,
            'Recording_Status': 'Completed'
          });
        }

        if (window.$zoho.salesiq.visitor && typeof window.$zoho.salesiq.visitor.chatmessage === 'function') {
          window.$zoho.salesiq.visitor.chatmessage(`🟢 Recording finished! Captured ${this.logs.length} log(s) and ${this.networkErrors.length} network error(s). Logs attached.`);
        } else if (window.$zoho.salesiq.visitor && typeof window.$zoho.salesiq.visitor.question === 'function') {
          window.$zoho.salesiq.visitor.question(`🟢 Recording finished! Captured ${this.logs.length} log(s) and ${this.networkErrors.length} network error(s). Logs attached.`);
        }
      } catch (err) {
        console.error('GlitchRecorder: Failed to dispatch logs to SalesIQ', err);
      }
    }

    console.log('🟢 GlitchRecorder: Recording stopped and telemetry sent.', capturedData);
    return capturedData;
  }

  safeStringify(obj) {
    try {
      return JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (key === 'headers' || key === 'config') return undefined; // Omit large redundant objects
        }
        return value;
      });
    } catch (e) {
      return '[Unserializable Object]';
    }
  }
}

const GlitchRecorder = new GlitchRecorderService();
window.GlitchRecorder = GlitchRecorder;

export default GlitchRecorder;
